import React, { useEffect, useState, useRef, useCallback } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "videojs-contrib-ads";
import "videojs-ima";
import "videojs-ima/dist/videojs.ima.css";
import "./MusicVideoFeed.css";
import DiscoverFeedHeader from "./components/DiscoverFeedHeader";
import { Spinner } from "reactstrap";
import { getVideoMimeType, copyToClipboard } from "./helpers/globals";
import thumbsdownEmoji from "./assets/thumbsdown-emoji.png";
import filterIcon from "./assets/filter-icon.png";
import GenreFilter from "./components/GenreFilter";
import { getPlaylist, getPlaylistGenres, searchVideos } from "./service/featured";
import useVideoAnalytics from "./hooks/useVideoAnalytics";
import { toggleBookmark, toggleDislike } from "./service/auth";
import { useAuth } from "./components/AuthProvider";
import PartyPassPromotionModal from "./components/PartyPassPromotionModal";
import { initiatePartyPassPurchase } from "./utils/partyPass";
import { getVideoUrl } from "./utils/urlHelpers";
import FullscreenFeedPlayer from "./components/FullscreenFeedPlayer";

// Clip settings
const CLIP_START_TIME = 0;
const CLIP_END_TIME = 20;
const SEARCH_DEBOUNCE_MS = 400;
const DISCOVER_STICKY_HEADER_HEIGHT_PX = 100;
const FREE_USER_AD_TAG_URL =
  "https://pubads.g.doubleclick.net/gampad/ads?iu=/22978710279/halfpage-for-video&description_url=[placeholder]&tfcd=0&npa=0&sz=640x480&ciu_szs=300x600&gdfp_req=1&output=vast&env=vp&unviewed_position_start=1&impl=s&correlator=";

// Scroll / gesture tuning (easy to adjust)
const SWIPE_THRESHOLD = 40; // pixels touch delta to count as swipe

const THUMBNAIL_HOLD_MS = 500;

// Transform playlist video data to feed format with clip times
const transformVideoToFeedFormat = (video) => {
  const totalDuration = Number(video.duration) || 180;
  const fallbackId = video.id ?? video.video_id ?? `${video.title}-${video.artist}`;

  return {
    id: fallbackId.toString(),
    videoUUID: video.video_id || fallbackId.toString(),
    title: video.title || "Untitled",
    artist: video.artist || "Unknown Artist",
    videoUrl: video.video_url,
    thumbnailUrl: video.thumbnail_url,
    clipStartTime: CLIP_START_TIME,
    clipEndTime: totalDuration, // Play full video instead of 20-second clip
    totalDuration: totalDuration,
    isBookmarked: video.is_bookmarked || false,
    isDisliked: video.is_disliked || false,
    genre: video.genre || null,
  };
};

const extractSearchResults = (payload) => {
  const candidates = [
    payload?.data?.videos,
    payload?.data?.results,
    Array.isArray(payload?.data) ? payload.data : null,
    payload?.videos,
    payload?.results,
    Array.isArray(payload) ? payload : null,
  ];

  return candidates.find(Array.isArray) || [];
};

const extractSearchPagination = (payload) => {
  const paginationSource = payload?.data?.data || payload || {};

  return {
    page: Number(paginationSource.page) || 1,
    perPage: Number(paginationSource.per_page) || 50,
    total: Number(paginationSource.total) || 0,
    totalPages: Number(paginationSource.total_pages) || 1,
  };
};

const isPlayableFeedVideo = (video) => {
  return Boolean(video.videoUrl && video.videoUUID);
};

// Individual Feed Video Card Component
// Double-tap timing threshold in ms
const DOUBLE_TAP_DELAY = 300;

const FeedVideoCard = ({
  video,
  isVisible,
  onLike,
  onDislike,
  onWatchFull,
  onSkipToNext,
  onFreeFullVideoClick,
  isLiked,
  isDisliked,
  isMutedGlobally,
  setIsMutedGlobally,
  isShowingSearchResults,
  onToggleFullscreen,
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const starBtnRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [clipEnded, setClipEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showStarAnimation, setShowStarAnimation] = useState(false);
  const [animationStyle, setAnimationStyle] = useState({});
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);
  const [showThumbsdownOverlay, setShowThumbsdownOverlay] = useState(false);
  const [showStarOverlay, setShowStarOverlay] = useState(false);
  const [isPlayingFullInline, setIsPlayingFullInline] = useState(false);
  const [isWaitingToAutoplay, setIsWaitingToAutoplay] = useState(false);

  // Double-tap detection
  const lastTapRef = useRef(0);
  const tapTimeoutRef = useRef(null);
  const isPlayingFullInlineRef = useRef(false);
  const imaInstanceRef = useRef(null);
  const autoplayTimeoutRef = useRef(null);

  // Video analytics tracking
  const analytics = useVideoAnalytics();
  const { hasPartyPass } = useAuth();

  // Handle copy link
  const handleCopyLink = (e) => {
    e.stopPropagation();
    const videoUrl = `${window.location.protocol}//${window.location.host}${getVideoUrl(video)}`;
    copyToClipboard(videoUrl);

    // Show copied feedback
    setShowCopiedFeedback(true);
    setTimeout(() => setShowCopiedFeedback(false), 2000);
  };

  // Handle star click with animation
  const handleStarClick = (e) => {
    e.stopPropagation();

    if (!isLiked) {
      // Show star emoji overlay
      setShowStarOverlay(true);
      setTimeout(() => setShowStarOverlay(false), 800);

      // Get the star button position
      const starBtn = starBtnRef.current;
      if (starBtn) {
        const rect = starBtn.getBoundingClientRect();

        // Calculate animation start position (relative to viewport)
        setAnimationStyle({
          '--start-x': `${rect.left + rect.width / 2}px`,
          '--start-y': `${rect.top + rect.height / 2}px`,
        });

        // Trigger animation
        setShowStarAnimation(true);

        // Trigger emoji bounce after 0.2s (when star reaches the emoji)
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('starAddedToLibrary'));
        }, 200);

        // Remove animation after it completes
        setTimeout(() => {
          setShowStarAnimation(false);
        }, 800);
      }
    }

    onLike(video.id);
  };

  const clipDuration = video.clipEndTime - video.clipStartTime;

  // Initialize video player
  useEffect(() => {
    if (videoRef.current && !playerRef.current) {
      const videoOptions = {
        controls: false,
        autoplay: false,
        muted: isMutedGlobally,
        loop: false,
        preload: "metadata",
        fluid: false,
      };

      playerRef.current = videojs(videoRef.current, videoOptions, function () {
        // Set initial time to clip start
        this.currentTime(video.clipStartTime);
      });

      // Handle time update for progress and clip end detection
      playerRef.current.on("timeupdate", () => {
        const currentTime = playerRef.current.currentTime();

        if (isPlayingFullInlineRef.current) {
          // For full video: track progress across entire video
          const duration = playerRef.current.duration();
          if (duration > 0) {
            setProgress((currentTime / duration) * 100);
          }
        } else {
          // Track progress across entire video (since clipEndTime is now totalDuration)
          const clipProgress = ((currentTime - video.clipStartTime) / clipDuration) * 100;
          setProgress(Math.min(Math.max(clipProgress, 0), 100));

          // No longer pause at clip end - let the video play to completion
          // The "ended" event will handle auto-advancing to the next video
        }
      });

      playerRef.current.on("play", () => {
        setIsPlaying(true);
        analytics.start(video.id);
        if (autoplayTimeoutRef.current) {
          clearTimeout(autoplayTimeoutRef.current);
          autoplayTimeoutRef.current = null;
        }
        setIsWaitingToAutoplay(false);
      });
      playerRef.current.on("pause", () => {
        setIsPlaying(false);
        analytics.pause();
      });

      playerRef.current.on("ended", () => {
        // When video ends, record analytics and auto-advance to next video
        setIsPlaying(false);
        analytics.record();

        // Auto-advance to next video after a brief delay
        setTimeout(() => {
          onSkipToNext?.();
        }, 500);
      });
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [video.clipStartTime, video.clipEndTime, clipDuration, video.id]);

  // Handle visibility changes (autoplay when visible)
  useEffect(() => {
    if (playerRef.current) {
      if (isVisible) {
        // If not playing full inline, reset to clip and hold on the
        // thumbnail briefly before autoplaying
        if (!isPlayingFullInline) {
          setClipEnded(false);
          setProgress(0);
          playerRef.current.currentTime(video.clipStartTime);
          setIsWaitingToAutoplay(true);

          autoplayTimeoutRef.current = setTimeout(() => {
            autoplayTimeoutRef.current = null;
            setIsWaitingToAutoplay(false);
            if (playerRef.current) {
              playerRef.current.play().catch(console.error);
            }
          }, THUMBNAIL_HOLD_MS);
        }
      } else {
        // Scrolled out of view before the hold finished: cancel the pending autoplay
        if (autoplayTimeoutRef.current) {
          clearTimeout(autoplayTimeoutRef.current);
          autoplayTimeoutRef.current = null;
        }
        setIsWaitingToAutoplay(false);

        // When scrolled out of view: pause and reset inline state
        playerRef.current.pause();
        analytics.record();
        if (isPlayingFullInline) {
          playerRef.current.currentTime(video.clipStartTime);
          setProgress(0);
          setClipEnded(false);
          setIsPlayingFullInline(false);
          isPlayingFullInlineRef.current = false;
        }
      }
    }

    return () => {
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
        autoplayTimeoutRef.current = null;
      }
    };
  }, [isVisible, video.clipStartTime, isPlayingFullInline]);

  // Cleanup tap timeout on unmount
  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.muted(isMutedGlobally);
    }
  }, [isMutedGlobally]);



  // Spacebar to pause/play when this video is visible
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space' && isVisible && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        if (playerRef.current && !clipEnded) {
          if (playerRef.current.paused()) {
            playerRef.current.play().catch(console.error);
          } else {
            playerRef.current.pause();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isVisible, clipEnded]);

  // Handle play full video inline (without navigation)
  const handlePlayFullInline = () => {

    if (!playerRef.current || !video.videoUrl) return;

    // Pause any current playback
    playerRef.current.pause();

    // Switch to full video source
    const mimeType = getVideoMimeType(video.videoUrl);
    playerRef.current.src({ src: video.videoUrl, type: mimeType });

    // Reset to start of full video and set up for full play
    playerRef.current.currentTime(0);
    setProgress(0);
    setClipEnded(false);
    setIsPlayingFullInline(true);
    isPlayingFullInlineRef.current = true;
    // setIsMuted(false); // Unmute for full video
    // playerRef.current.muted(false);
    // setIsMutedGlobally(false);

    // Initialize and request IMA ad for free users, with safe fallback
    if (!hasPartyPass) {
      try {
        const imaOptions = { adTagUrl: FREE_USER_AD_TAG_URL };

        let imaInstance = null;
        if (playerRef.current && typeof playerRef.current.ima === "function") {
          imaInstance = playerRef.current.ima(imaOptions);
        } else if (playerRef.current && playerRef.current.ima) {
          imaInstance = playerRef.current.ima;
        }

        imaInstanceRef.current = imaInstance;

        if (imaInstance && typeof imaInstance.initializeAdDisplayContainer === "function") {
          try {
            imaInstance.initializeAdDisplayContainer();
          } catch (err) {
            console.warn("initializeAdDisplayContainer failed:", err);
          }
        }

        // Attach ad event listeners so we can resume content after ads
        const onAdStarted = () => {
          console.log("Ad started");
        };
        const onAdEnded = () => {
          console.log("Ad ended");
          try {
            playerRef.current.off("ads-ad-started", onAdStarted);
            playerRef.current.off("ads-ad-ended", onAdEnded);
          } catch (e) {
            /* ignore */
          }
          // Resume content playback after ad
          playerRef.current.play().catch(console.error);
        };

        try {
          playerRef.current.on("ads-ad-started", onAdStarted);
          playerRef.current.on("ads-ad-ended", onAdEnded);
        } catch (e) {
          console.warn("Failed to attach ad event listeners:", e);
        }

        if (imaInstance && typeof imaInstance.requestAds === "function") {
          try {
            imaInstance.requestAds();
            console.log("IMA requestAds() invoked on instance");
            // Do not call play here; IMA will control playback lifecycle
            return;
          } catch (err) {
            console.warn("imaInstance.requestAds() error:", err);
          }
        }

        // If requestAds isn't available, fall back to starting playback
        console.log("IMA requestAds not available, falling back to content playback");
        playerRef.current.play().catch(console.error);
        return;
      } catch (err) {
        console.warn("IMA initialization failed:", err);
        playerRef.current.play().catch(console.error);
        return;
      }
    }

    // For party pass users or if IMA not used, just play content
    playerRef.current.play().catch(console.error);
  };


  // Toggle mute
  const handleToggleMute = () => {
    setIsMutedGlobally(prev => !prev);
  };

  // Handle double-tap to like
  const handleDoubleTapLike = () => {
    if (!isLiked) {
      // Show star emoji overlay
      setShowStarOverlay(true);
      setTimeout(() => setShowStarOverlay(false), 800);

      // Trigger the like action
      onLike(video.id);
    }
  };

  // Single tap to pause/play
  const handleSingleTap = () => {
    if (playerRef.current && !clipEnded) {
      if (playerRef.current.paused()) {
        playerRef.current.play().catch(console.error);
      } else {
        playerRef.current.pause();
      }
    }
  };

  // Tap handler with double-tap detection
  const handleVideoTap = (e) => {
    if (e.target.closest('.feed-action-buttons-row') ||
      e.target.closest('.watch-now-overlay') ||
      e.target.closest('.feed-mute-btn') ||
      e.target.closest('.feed-fullscreen-btn')) {
      return;
    }

    const now = Date.now();
    const timeSinceLastTap = now - lastTapRef.current;

    if (timeSinceLastTap < DOUBLE_TAP_DELAY && timeSinceLastTap > 0) {
      // Double tap detected
      clearTimeout(tapTimeoutRef.current);
      handleDoubleTapLike();
      lastTapRef.current = 0; // Reset to prevent triple-tap issues
    } else {
      // First tap - wait to see if it's a double tap
      lastTapRef.current = now;
      tapTimeoutRef.current = setTimeout(() => {
        handleSingleTap();
      }, DOUBLE_TAP_DELAY);
    }
  };

  const handleFullVideoClick = (e) => {
    e.stopPropagation();

    if (hasPartyPass) {
      handlePlayFullInline();
    } else {
      onFreeFullVideoClick(handlePlayFullInline);
    }
  }

  return (
    <div className="feed-video-card" onClick={handleVideoTap}>
      {/* Video Player */}
      <div className="feed-video-container">
        <video
          ref={videoRef}
          className="video-js feed-video-player"
          playsInline
          poster={video.thumbnailUrl}
        >
          <source src={video.videoUrl} type={getVideoMimeType(video.videoUrl)} />
        </video>

        {/* Mute Toggle Button (upper left) */}
        <button
          className={`feed-mute-btn ${isMutedGlobally ? 'muted' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            handleToggleMute();
          }}
          title={isMutedGlobally ? "Unmute" : "Mute"}
        >
          {isMutedGlobally ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          )}
        </button>

        {/* Fullscreen Toggle Button (bottom right) */}
        <button
          className="feed-fullscreen-btn"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFullscreen?.();
          }}
          title="Enter fullscreen"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
        </button>

        {/* Play Button Overlay */}
        {!isPlaying && !clipEnded && !isWaitingToAutoplay && (
          <button
            className="feed-play-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (playerRef.current) {
                playerRef.current.currentTime(video.clipStartTime);
                playerRef.current.play().catch(console.error);
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </button>
        )}

        {/* Copied Link Feedback */}
        {showCopiedFeedback && (
          <div className="copied-link-overlay">
            <span className="copied-link-text">Copied</span>
          </div>
        )}

        {/* Star Overlay */}
        {showStarOverlay && (
          <div className="star-overlay">
            <span className="star-emoji-overlay">⭐</span>
          </div>
        )}

        {/* Thumbs Down Overlay */}
        {showThumbsdownOverlay && (
          <div className="thumbsdown-overlay">
            <img src={thumbsdownEmoji} alt="Thumbs down" className="thumbsdown-emoji" />
          </div>
        )}

        {/* Watch Now Overlay removed - videos now play in full automatically */}

        {/* Progress Bar */}
        <div className="feed-progress-bar">
          <div
            className="feed-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Metadata */}
      <div className="feed-video-metadata">
        <div className="feed-video-info">
          <h3 className="feed-video-title">{video.title}</h3>
          <p className="feed-video-artist">{video.artist}</p>
        </div>
        {/* FULL VIDEO button removed - videos now play in full automatically */}
      </div>

      {/* Action Buttons Row (below metadata) */}
      <div className="feed-action-buttons-row">
        <button
          ref={starBtnRef}
          className={`feed-action-btn like-btn ${isLiked ? 'active' : ''}`}
          onClick={handleStarClick}
          title="Save to library"
        >
          <svg viewBox="0 0 24 24" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
        <button
          className={`feed-action-btn link-btn ${showCopiedFeedback ? 'copied' : ''}`}
          onClick={handleCopyLink}
          title="Copy link to share"
        >
          {showCopiedFeedback ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          )}
        </button>
        <button
          className={`feed-action-btn dislike-btn ${isDisliked ? 'active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onDislike(video.id);
            setShowThumbsdownOverlay(true);

            // Hide overlay and skip to next after animation
            setTimeout(() => {
              setShowThumbsdownOverlay(false);
              onSkipToNext?.();
            }, 800);
          }}
          title="Not for me"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
          </svg>
        </button>
      </div>

      {/* Flying Star Animation */}
      {showStarAnimation && (
        <div className="flying-star-container" style={animationStyle}>
          <div className="flying-star">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          {/* Sparkle particles */}
          <div className="sparkle sparkle-1"></div>
          <div className="sparkle sparkle-2"></div>
          <div className="sparkle sparkle-3"></div>
          <div className="sparkle sparkle-4"></div>
          <div className="sparkle sparkle-5"></div>
        </div>
      )}
    </div>
  );
};

// Main Music Video Feed Component
const MusicVideoFeed = () => {
  const [defaultFeedVideos, setDefaultFeedVideos] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [visibleVideoId, setVisibleVideoId] = useState(null);
  const [likedVideos, setLikedVideos] = useState(new Set());
  const [dislikedVideos, setDislikedVideos] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isStickyHeader, setIsStickyHeader] = useState(false);
  const [isHeaderShrunk, setIsHeaderShrunk] = useState(true);
  const [isMutedGlobally, setIsMutedGlobally] = useState(true);
  const feedPageRef = useRef(null);
  const feedContainerRef = useRef(null);
  const feedScrollContainerRef = useRef(null);
  const feedSearchShellRef = useRef(null);
  const observerRef = useRef(null);
  const cardRefsMap = useRef(new Map());
  const currentIndexRef = useRef(0);
  const activeFeedVideosRef = useRef([]);
  const searchRequestIdRef = useRef(0);
  const searchFetchInFlightRef = useRef(false);
  const searchInputRef = useRef(null);
  const feedScrollNodeRef = useRef(null);
  const isStickyHeaderRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const scrollAnchorRef = useRef(null);

  const [isFullscreenPlayerOpen, setIsFullscreenPlayerOpen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isLoadingMoreSearchResults, setIsLoadingMoreSearchResults] = useState(false);
  const [playlistName, setPlaylistName] = useState("");
  const [searchError, setSearchError] = useState("");
  const [showPartyPassPrompt, setShowPartyPassPrompt] = useState(false);
  const [buyPassLaterCallback, setBuyPassLaterCallback] = useState(() => { });
  const [isMobile, setIsMobile] = useState(false);
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState(new Set());
  const [availableGenres, setAvailableGenres] = useState([]);
  const [searchPagination, setSearchPagination] = useState({
    page: 1,
    perPage: 50,
    total: 0,
    totalPages: 1,
  });
  const trimmedSearchTerm = searchTerm.trim();
  const isShowingSearchResults = trimmedSearchTerm.length > 0;
  const isSearchPending = isShowingSearchResults && trimmedSearchTerm !== debouncedSearchTerm;
  const activeFeedVideos = isShowingSearchResults ? searchResults : defaultFeedVideos;
  const activeFeedTitle = isShowingSearchResults
    ? `Search Results${trimmedSearchTerm ? ` for "${trimmedSearchTerm}"` : ""}`
    : playlistName;
  const hasMoreSearchResults = searchPagination.page < searchPagination.totalPages;
  const headerIsCompact = isStickyHeader || isHeaderShrunk;

  const scrollToTop = useCallback(() => {
    const scrollContainer = feedScrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  const scrollToIndex = useCallback(
    (nextIndex) => {
      if (nextIndex < 0 || nextIndex >= activeFeedVideos.length) {
        return;
      }
      currentIndexRef.current = nextIndex;
      const scrollContainer = feedScrollContainerRef.current;
      if (!scrollContainer) return;

      const nextVideoId = activeFeedVideos[nextIndex].id;
      const nextCardNode = cardRefsMap.current.get(nextVideoId);
      if (nextCardNode) {
        scrollContainer.scrollTo({
          top: nextCardNode.offsetTop,
          behavior: 'smooth',
        });
      }
    },
    [activeFeedVideos]
  );

  const handleOpenFullscreenPlayer = useCallback(
    (videoId) => {
      const idx = activeFeedVideos.findIndex((v) => v.id === videoId);
      const targetIndex = idx >= 0 ? idx : currentIndexRef.current || 0;
      setFullscreenIndex(targetIndex);
      setIsFullscreenPlayerOpen(true);
    },
    [activeFeedVideos]
  );

  const handleCloseFullscreenPlayer = useCallback(() => {
    setIsFullscreenPlayerOpen(false);
    if (activeFeedVideos[fullscreenIndex]) {
      const currentVideo = activeFeedVideos[fullscreenIndex];
      setVisibleVideoId(currentVideo.id);
      currentIndexRef.current = fullscreenIndex;
      scrollToIndex(fullscreenIndex);
    }
  }, [activeFeedVideos, fullscreenIndex, scrollToIndex]);

  const handleFullscreenIndexChange = useCallback(
    (newIndex) => {
      setFullscreenIndex(newIndex);
      currentIndexRef.current = newIndex;
      if (activeFeedVideos[newIndex]) {
        setVisibleVideoId(activeFeedVideos[newIndex].id);
      }
    },
    [activeFeedVideos]
  );
  const handleFreeFullVideoClick = useCallback((skipCallback) => {
    console.log({ skipCallback });
    setBuyPassLaterCallback(() => skipCallback);
    setShowPartyPassPrompt(true);
  }, []);

  const handleFilterClick = useCallback(() => {
    setIsFilterPopoverOpen((prev) => !prev);
  }, []);

  const handleGenreToggle = useCallback((genre) => {
    setSelectedGenres((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(genre)) {
        newSet.delete(genre);
      } else {
        newSet.add(genre);
      }
      return newSet;
    });
  }, []);

  // Load available genres from API for playlist 323
  useEffect(() => {
    getPlaylistGenres(323)
      .then((response) => {
        const genres = response?.data?.data;
        if (Array.isArray(genres) && genres.length > 0) {
          const cleanGenres = Array.from(new Set(genres.filter(Boolean))).sort();
          setAvailableGenres(cleanGenres);
        }
      })
      .catch((error) => {
        console.error("Error fetching genres:", error);
      });
  }, []);

  // Load feed videos from playlist 323 with genre filter
  useEffect(() => {
    setIsLoading(true);

    const fetchVideos = async () => {
      try {
        const params = {};
        if (selectedGenres.size > 0) {
          params.genre = Array.from(selectedGenres);
        }

        const response = await getPlaylist(323, params);
        const { data } = response.data;
        setPlaylistName(data?.name || "Feed");

        const sortedVideos = (data?.videos || [])
          .sort((a, b) => a.order - b.order)
          .map(transformVideoToFeedFormat)
          .filter(isPlayableFeedVideo);

        setDefaultFeedVideos(sortedVideos);
        setLikedVideos(new Set(sortedVideos.filter(v => v.isBookmarked).map(v => v.id)));
        setDislikedVideos(new Set(sortedVideos.filter(v => v.isDisliked).map(v => v.id)));

        if (selectedGenres.size === 0 && sortedVideos.length > 0) {
          setAvailableGenres((prev) => {
            if (prev.length > 0) return prev;
            return Array.from(new Set(sortedVideos.map(v => v.genre).filter(Boolean))).sort();
          });
        }
      } catch (error) {
        console.error("Error fetching feed videos:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideos();
  }, [selectedGenres]);

  // Detect mobile device on client-side
  useEffect(() => {
    setIsMobile(window.screen.width <= 800);
  }, []);

  // Load / Refresh Revive Adserver script
  useEffect(() => {
    if (isLoading) return;

    const REVIVE_ID = "727bec5e09208690b050ccfc6a45d384";
    const triggerRevive = () => {
      if (window.reviveAsync && window.reviveAsync[REVIVE_ID]) {
        try {
          window.reviveAsync[REVIVE_ID].refresh();
        } catch (e) {
          console.error("Error refreshing Revive ads:", e);
        }
      }
    };

    if (!document.getElementById("revive-async-script")) {
      const script = document.createElement("script");
      script.id = "revive-async-script";
      script.src = "//servedby.revive-adserver.net/asyncjs.php";
      script.async = true;
      script.onload = () => {
        setTimeout(triggerRevive, 50);
      };
      document.body.appendChild(script);
    } else {
      setTimeout(triggerRevive, 50);
    }
  }, [isLoading, isMobile]);

  // Debounce keyword input before searching
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchSearchPage = useCallback((keyword, pageToLoad, appendResults = false) => {
    searchFetchInFlightRef.current = true;
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;

    if (appendResults) {
      setIsLoadingMoreSearchResults(true);
    } else {
      setIsSearchLoading(true);
      setSearchError("");
    }

    return searchVideos({
      search_query: keyword,
      // page: pageToLoad,
    })
      .then((response) => {
        if (requestId !== searchRequestIdRef.current) {
          return;
        }

        const results = extractSearchResults(response.data)
          .map(transformVideoToFeedFormat)
          .filter(isPlayableFeedVideo);
        const pagination = extractSearchPagination(response.data);

        setSearchPagination(pagination);
        setSearchResults((prev) => {
          if (!appendResults) {
            return results;
          }

          const existingIds = new Set(prev.map((video) => video.id));
          const nextPageResults = results.filter((video) => !existingIds.has(video.id));
          return [...prev, ...nextPageResults];
        });
      })
      .catch((error) => {
        if (requestId !== searchRequestIdRef.current) {
          return;
        }

        console.error("Error searching feed videos:", error);
        if (!appendResults) {
          setSearchResults([]);
          setSearchPagination({
            page: 1,
            perPage: 50,
            total: 0,
            totalPages: 1,
          });
        }
        setSearchError("We couldn't load search results right now.");
      })
      .finally(() => {
        if (requestId === searchRequestIdRef.current) {
          setIsSearchLoading(false);
          setIsLoadingMoreSearchResults(false);
        }
        searchFetchInFlightRef.current = false;
      });
  }, []);

  // Search discover feed by keyword
  useEffect(() => {
    const keyword = debouncedSearchTerm;

    if (!keyword) {
      searchRequestIdRef.current += 1;
      searchFetchInFlightRef.current = false;
      setSearchResults([]);
      setSearchError("");
      setIsSearchLoading(false);
      setIsLoadingMoreSearchResults(false);
      setSearchPagination({
        page: 1,
        perPage: 50,
        total: 0,
        totalPages: 1,
      });
      return;
    }

    setSearchPagination({
      page: 1,
      perPage: 50,
      total: 0,
      totalPages: 1,
    });
    fetchSearchPage(keyword, 1, false);
  }, [debouncedSearchTerm, fetchSearchPage]);

  // Keep a ref mirror of activeFeedVideos so callbacks created once (e.g. the
  // IntersectionObserver below) always read the *current* list instead of a
  // stale closure captured on mount.
  useEffect(() => {
    activeFeedVideosRef.current = activeFeedVideos;
  }, [activeFeedVideos]);

  // Set up Intersection Observer for autoplay detection
  useEffect(() => {
    const options = {
      root: null, // viewport
      rootMargin: "-45% 0px -45% 0px", // triggers when video is roughly centered
      threshold: 0,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      // During fast scrolling more than one card can briefly report as
      // "intersecting" in the same callback batch. Only act on the one that
      // is most centered (highest intersectionRatio) so we don't let an
      // outgoing card's stale entry overwrite the index we just settled on.
      const bestEntry = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

      if (bestEntry) {
        const videoId = bestEntry.target.dataset.videoId;
        setVisibleVideoId(videoId);
        // keep currentIndexRef synced to the visible video, always reading
        // the latest list via the ref (not the array closed over at mount)
        const idx = activeFeedVideosRef.current.findIndex(v => v.id === videoId);
        if (idx !== -1) {
          currentIndexRef.current = idx;
        }
      }
    }, options);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  // Reset scroll observers and autoplay target when the active feed changes
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    cardRefsMap.current = new Map();
    setVisibleVideoId(activeFeedVideos.length > 0 ? activeFeedVideos[0].id : null);
    currentIndexRef.current = -1;

    // On initial load, ensure scroll stays at top to show ad unit above first video
    if (isInitialLoadRef.current && feedScrollContainerRef.current) {
      feedScrollContainerRef.current.scrollTop = 0;
    }
  }, [activeFeedVideos]);

  const setStickyHeaderIfChanged = useCallback((nextStickyState) => {
    if (nextStickyState !== isStickyHeaderRef.current) {
      isStickyHeaderRef.current = nextStickyState;
      setIsStickyHeader(nextStickyState);
    }
  }, []);

  // Sticky header detection: switch when the large search shell scrolls out of view.
  // This avoids jumpiness from an arbitrary scrollTop threshold.
  useEffect(() => {
    const searchShell = feedSearchShellRef.current;
    const scrollContainer = feedScrollContainerRef.current;

    if (!searchShell) {
      return;
    }

    const isContainerScrollable = Boolean(
      scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight + 1
    );

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Sticky when the search shell is not meaningfully visible.
        setStickyHeaderIfChanged(!entry.isIntersecting);
      },
      {
        root: isContainerScrollable ? scrollContainer : null,
        // Treat the top area behind the sticky header as "not visible" so the
        // search icon appears as soon as the large search slips under it.
        rootMargin: `-${DISCOVER_STICKY_HEADER_HEIGHT_PX}px 0px 0px 0px`,
        threshold: [0, 0.01],
      }
    );

    observer.observe(searchShell);
    return () => observer.disconnect();
  }, [setStickyHeaderIfChanged]);

  // Load more paginated search results as the user nears the end of the list
  const handleFeedScroll = useCallback((event) => {
    const node = event.currentTarget;
    feedScrollNodeRef.current = node;

    if (!isHeaderShrunk && node.scrollTop > 0) {
      setIsHeaderShrunk(true);
    }

    if (
      !isShowingSearchResults ||
      isSearchPending ||
      isSearchLoading ||
      isLoadingMoreSearchResults ||
      searchFetchInFlightRef.current ||
      !hasMoreSearchResults
    ) {
      return;
    }
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;

    if (distanceFromBottom < 900) {
      fetchSearchPage(debouncedSearchTerm, searchPagination.page + 1, true);
    }
  }, [
    debouncedSearchTerm,
    fetchSearchPage,
    hasMoreSearchResults,
    isHeaderShrunk,
    isLoadingMoreSearchResults,
    isSearchLoading,
    isSearchPending,
    isShowingSearchResults,
    searchPagination.page,
  ]);

  // Observe video cards when they mount
  const observeVideoCard = useCallback((node, videoId) => {
    if (node && observerRef.current) {
      node.dataset.videoId = videoId;
      observerRef.current.observe(node);
      cardRefsMap.current.set(videoId, node);
    }
  }, []);



  // Wheel / touch handlers: wait for user to stop scrolling, then snap one item
  useEffect(() => {
    const node = feedScrollContainerRef.current;
    if (!node) return;

    let lastWheelDir = 0;
    let lastTouchDir = 0;
    let touchStartY = null;
    let scrollSettleTimeout = null;

    const onWheel = (e) => {
      // allow Ctrl+wheel (zoom)
      if (e.ctrlKey) return;
      // Mark initial load as complete on first wheel interaction
      isInitialLoadRef.current = false;
      // do not prevent default: allow native smooth scrolling
      if (e.deltaY > 0) lastWheelDir = 1;
      else if (e.deltaY < 0) lastWheelDir = -1;
    };

    const onTouchStart = (e) => {
      // Mark initial load as complete on first touch interaction
      isInitialLoadRef.current = false;
      touchStartY = e.touches?.[0]?.clientY ?? null;
      lastTouchDir = 0;
    };

    const onTouchMove = (e) => {
      const y = e.touches?.[0]?.clientY ?? null;
      if (y == null || touchStartY == null) return;
      const delta = touchStartY - y;
      if (Math.abs(delta) >= 5) lastTouchDir = delta > 0 ? 1 : -1;
    };

    const onTouchEnd = (e) => {
      if (touchStartY == null) return;
      const endY = e.changedTouches?.[0]?.clientY ?? null;
      if (endY == null) {
        touchStartY = null;
        return;
      }
      const delta = touchStartY - endY;
      if (Math.abs(delta) >= SWIPE_THRESHOLD) lastTouchDir = delta > 0 ? 1 : -1;
      touchStartY = null;
    };

    const onScrollSettle = () => {
      if (scrollSettleTimeout) clearTimeout(scrollSettleTimeout);
      scrollSettleTimeout = window.setTimeout(() => {
        // Skip auto-snapping on initial load to keep ad visible at top
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
          return;
        }

        const scrollTop = node.scrollTop;
        const containerCenter = scrollTop + node.clientHeight / 2;
        const dir = lastWheelDir || lastTouchDir || 0;

        // If resting at the top banner area and scrolling up
        if (scrollTop <= 60 && dir <= 0) {
          currentIndexRef.current = -1;
          if (activeFeedVideos[0]) setVisibleVideoId(activeFeedVideos[0].id);
          lastWheelDir = 0;
          lastTouchDir = 0;
          return;
        }

        let nearestIdx = 0;
        let nearestDist = Infinity;
        for (let i = 0; i < activeFeedVideos.length; i++) {
          const vid = activeFeedVideos[i];
          const cardNode = cardRefsMap.current.get(vid.id);
          if (!cardNode) continue;
          const cardCenter = cardNode.offsetTop + cardNode.clientHeight / 2;
          const dist = Math.abs(cardCenter - containerCenter);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearestIdx = i;
          }
        }

        // determine whether user moved far enough in that direction to advance
        let targetIdx = nearestIdx;
        if (dir !== 0) {
          const nearestCard = cardRefsMap.current.get(activeFeedVideos[nearestIdx]?.id);
          if (nearestCard) {
            const cardHeight = nearestCard.clientHeight || node.clientHeight;
            const cardCenter = nearestCard.offsetTop + cardHeight / 2;
            const deltaFromCenter = containerCenter - cardCenter; // positive means container center is below card center
            const threshold = Math.max(24, cardHeight * 0.25);
            if (dir > 0 && deltaFromCenter > threshold) {
              targetIdx = Math.min(nearestIdx + 1, activeFeedVideos.length - 1);
            } else if (dir < 0 && deltaFromCenter < -threshold) {
              targetIdx = Math.max(nearestIdx - 1, 0);
            } else {
              targetIdx = nearestIdx;
            }
          } else {
            targetIdx = Math.min(Math.max(0, nearestIdx + dir), activeFeedVideos.length - 1);
          }
        }

        currentIndexRef.current = targetIdx;
        const vid = activeFeedVideos[targetIdx];
        if (vid) {
          setVisibleVideoId(vid.id);
          if (targetIdx !== nearestIdx) {
            // only programmatically scroll when we decided to advance/back up
            scrollToIndex(targetIdx);
          }
        }

        // reset direction trackers
        lastWheelDir = 0;
        lastTouchDir = 0;
      }, 200);
    };

    node.addEventListener('wheel', onWheel, { passive: true });
    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: true });
    node.addEventListener('touchend', onTouchEnd, { passive: true });
    node.addEventListener('scroll', onScrollSettle, { passive: true });

    return () => {
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('scroll', onScrollSettle);
      if (scrollSettleTimeout) clearTimeout(scrollSettleTimeout);
    };
  }, [feedScrollContainerRef, activeFeedVideos, scrollToIndex]);

  // Keyboard navigation for desktop: move one item per arrow/page key
  useEffect(() => {
    const onKey = (e) => {
      if (isFullscreenPlayerOpen) return;
      // don't interfere with typing in inputs
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      const scrollContainer = feedScrollContainerRef.current;
      const currentScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
      const isAtTopBanner = currentScrollTop <= 60 || currentIndexRef.current === -1;

      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        if (isAtTopBanner) {
          // If viewing top ad banner, first ArrowDown brings Video 1 into full view
          currentIndexRef.current = 0;
          scrollToIndex(0);
        } else {
          const currentIdx = Math.max(0, currentIndexRef.current);
          const nextIdx = Math.min(currentIdx + 1, activeFeedVideos.length - 1);
          if (nextIdx !== currentIdx) {
            currentIndexRef.current = nextIdx;
            scrollToIndex(nextIdx);
          }
        }
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        const currentIdx = currentIndexRef.current;
        if (currentIdx <= 0) {
          // If at Video 1, ArrowUp scrolls back up to the Ad Banner view
          currentIndexRef.current = -1;
          scrollToTop();
        } else {
          const prevIdx = currentIdx - 1;
          currentIndexRef.current = prevIdx;
          scrollToIndex(prevIdx);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        currentIndexRef.current = -1;
        scrollToTop();
      } else if (e.key === 'End') {
        e.preventDefault();
        currentIndexRef.current = activeFeedVideos.length - 1;
        scrollToIndex(activeFeedVideos.length - 1);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeFeedVideos, isFullscreenPlayerOpen, scrollToIndex, scrollToTop]);

  // Handle like action
  const handleLike = async (videoId) => {
    const authToken = localStorage.getItem("authToken");
    const wasLiked = likedVideos.has(videoId);

    // Optimistic update
    setLikedVideos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
        // Remove from disliked if present
        // setDislikedVideos((d) => {
        //   const newD = new Set(d);
        //   newD.delete(videoId);
        //   return newD;
        // });
      }
      return newSet;
    });

    // Call API if authenticated
    if (authToken) {
      try {
        const response = await toggleBookmark(videoId);
        const bookmarked = response.data?.data?.bookmarked === 1;

        // Update state based on API response
        setLikedVideos((prev) => {
          const newSet = new Set(prev);
          if (bookmarked) {
            newSet.add(videoId);
          } else {
            newSet.delete(videoId);
          }
          return newSet;
        });
      } catch (error) {
        console.error("Failed to toggle bookmark:", error);
        // Revert on error
        setLikedVideos((prev) => {
          const newSet = new Set(prev);
          if (wasLiked) {
            newSet.add(videoId);
          } else {
            newSet.delete(videoId);
          }
          return newSet;
        });
      }
    }
  };

  // Handle dislike action
  const handleDislike = async (videoId) => {
    const authToken = localStorage.getItem("authToken");
    const wasDisliked = dislikedVideos.has(videoId);

    setDislikedVideos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
        // Remove from liked if present
        // setLikedVideos((l) => {
        //   const newL = new Set(l);
        //   newL.delete(videoId);
        //   return newL;
        // });
      }
      return newSet;
    });

    // Call API if authenticated
    if (authToken) {
      try {
        const response = await toggleDislike(videoId);
        const disliked = response.data?.data?.disliked === 1;

        // Update state based on API response
        setDislikedVideos((prev) => {
          const newSet = new Set(prev);
          if (disliked) {
            newSet.add(videoId);
          } else {
            newSet.delete(videoId);
          }
          return newSet;
        });
      } catch (error) {
        console.error("Failed to toggle dislike:", error);
        // Revert on error
        setDislikedVideos((prev) => {
          const newSet = new Set(prev);
          if (wasDisliked) {
            newSet.add(videoId);
          } else {
            newSet.delete(videoId);
          }
          return newSet;
        });
      }
    } else {
      // No auth token
    }
  };

  // Handle watch full video
  const handleWatchFull = (video) => {
    // Navigate to full video page using SEO-friendly URL
    window.location.href = getVideoUrl(video);
  };

  // Handle skip to next video
  const handleSkipToNext = useCallback((currentVideoId) => {
    const currentIndex = activeFeedVideos.findIndex(v => v.id === currentVideoId);
    if (currentIndex < activeFeedVideos.length - 1) {
      scrollToIndex(currentIndex + 1);
    }
  }, [activeFeedVideos, scrollToIndex]);

  return (
    <div
      ref={feedPageRef}
      className={`music-video-feed-page${headerIsCompact ? " music-video-feed-page--sticky-header" : ""}`}
    >
      <DiscoverFeedHeader
        isStickyHeader={headerIsCompact}
      />

      {/* Floating Genre Filter button */}
      <button
        type="button"
        className={`feed-filter-btn${isFilterPopoverOpen ? ' active' : ''}`}
        onClick={handleFilterClick}
        id="genreFilterBtn"
        aria-label="Filter by genre"
        title="Filter by genre"
      >
        <img src={filterIcon} alt="Filter" className="feed-filter-icon" />
        {selectedGenres.size > 0 && (
          <span className="feed-filter-badge">{selectedGenres.size}</span>
        )}
      </button>

      <GenreFilter
        isOpen={isFilterPopoverOpen}
        toggle={() => setIsFilterPopoverOpen(false)}
        targetId="genreFilterBtn"
        genres={availableGenres}
        selectedGenres={selectedGenres}
        onGenreToggle={handleGenreToggle}
        onClear={() => setSelectedGenres(new Set())}
      />

      {/* Search Title */}
      {isShowingSearchResults && (
        <div className="feed-playlist-header">
          <h2 className="feed-playlist-title">{activeFeedTitle}</h2>
          <p className="feed-playlist-subtitle">
            {isSearchPending || isSearchLoading
              ? `Searching for "${trimmedSearchTerm}"...`
              : ''}
          </p>
        </div>
      )}

      <div className="music-video-feed-container" ref={feedContainerRef}>
        {isLoading ? (
          <div className="feed-loading">
            <Spinner color="warning">Loading...</Spinner>
          </div>
        ) : isShowingSearchResults && searchError && searchResults.length === 0 ? (
          <div className="feed-empty-state">
            <h3>Search is unavailable</h3>
            <p>{searchError}</p>
          </div>
        ) : isShowingSearchResults && (isSearchPending || (isSearchLoading && searchResults.length === 0)) ? (
          <div className="feed-loading">
            <Spinner color="warning">Searching...</Spinner>
          </div>
        ) : activeFeedVideos.length === 0 ? (
          <div className="feed-empty-state">
            <h3>
              {selectedGenres.size > 0
                ? "No videos match selected genres"
                : isShowingSearchResults
                  ? "No search results"
                  : "No videos available"}
            </h3>
            <p>
              {selectedGenres.size > 0
                ? "Try selecting different genres."
                : isShowingSearchResults
                  ? "Try a different keyword to load more music videos."
                  : "The discover feed is empty right now."}
            </p>
          </div>
        ) : (
          <div
            className="feed-scroll-container"
            ref={feedScrollContainerRef}
            style={{ "--discover-header-height": `${DISCOVER_STICKY_HEADER_HEIGHT_PX}px` }}
            onScroll={handleFeedScroll}
          >
            {/* Scroll anchor div - page loads to this position */}
            <div
              ref={scrollAnchorRef}
              style={{
                height: "1px",
                backgroundColor: "#000"
              }}
            />

            {/* Fixed Ad Banner - Inside scroll container so it scrolls with content */}
            <div
              className="feed-ad-banner"
              style={{
                backgroundColor: "#000",
                textAlign: "center",
                padding: "20px 0",
                borderBottom: "1px solid #333"
              }}
            >
              {isMobile ? (
                <ins data-revive-zoneid="30392" data-revive-id="727bec5e09208690b050ccfc6a45d384"></ins>
              ) : (
                <ins data-revive-zoneid="30381" data-revive-id="727bec5e09208690b050ccfc6a45d384"></ins>
              )}
            </div>

            {/* Search Shell - Moved inside scroll container to scroll with feed */}
            <div className="feed-search-shell" ref={feedSearchShellRef}>
              <div className="feed-search-input-wrap">
                <svg
                  className="feed-search-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="16.65" y1="16.65" x2="21" y2="21" />
                </svg>
                <input
                  type="text"
                  className="feed-search-input"
                  placeholder="What do you want to play?"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search discover feed"
                  ref={searchInputRef}
                />
                {(searchTerm || isSearchLoading) && (
                  <button
                    type="button"
                    className="feed-search-clear"
                    onClick={() => setSearchTerm("")}
                    aria-label={isSearchLoading ? "Searching" : "Clear search"}
                    disabled={isSearchLoading && !searchTerm}
                  >
                    {isSearchLoading ? <Spinner size="sm" color="light" /> : "Clear"}
                  </button>
                )}
              </div>
              <div className="feed-search-overlay__status">
                {isSearchPending ? (
                  <>
                    <Spinner size="sm" color="warning" />
                    <span>Searching for "{trimmedSearchTerm}"...</span>
                  </>
                ) : isSearchLoading ? (
                  <>
                    <Spinner size="sm" color="warning" />
                    <span>Loading results for "{debouncedSearchTerm}"...</span>
                  </>
                ) : isShowingSearchResults && debouncedSearchTerm ? (
                  <span>{searchResults.length} results for "{debouncedSearchTerm}"</span>
                ) : null}
              </div>
            </div>

            {activeFeedVideos.map((video) => (
              <div
                key={video.id}
                ref={(node) => observeVideoCard(node, video.id)}
                className="feed-card-wrapper"
              >
                <FeedVideoCard
                  video={video}
                  isVisible={!isFullscreenPlayerOpen && visibleVideoId === video.id}
                  onLike={handleLike}
                  onDislike={handleDislike}
                  onWatchFull={handleWatchFull}
                  onSkipToNext={() => handleSkipToNext(video.id)}
                  onFreeFullVideoClick={handleFreeFullVideoClick}
                  isLiked={likedVideos.has(video.id)}
                  isDisliked={dislikedVideos.has(video.id)}
                  isMutedGlobally={isMutedGlobally}
                  setIsMutedGlobally={setIsMutedGlobally}
                  isShowingSearchResults={isShowingSearchResults}
                  onToggleFullscreen={() => handleOpenFullscreenPlayer(video.id)}
                />
              </div>
            ))}
            {isShowingSearchResults && (
              <div className="feed-search-status">
                {(isSearchPending || isSearchLoading) && (
                  <>
                    <Spinner size="sm" color="warning" />
                    <span>Searching for "{trimmedSearchTerm}"...</span>
                  </>
                )}
                {searchError ? <span>{searchError}</span> : null}
              </div>
            )}
          </div>
        )}
      </div>
      <PartyPassPromotionModal
        isOpen={showPartyPassPrompt}
        toggle={() => {
          setShowPartyPassPrompt(false);
          buyPassLaterCallback?.();
        }}
        onUnlockClick={initiatePartyPassPurchase}
        title="Party Pass"
        message="Buy your Party Pass to stream any videos on demand"
        heading="Stream On Demand"
        description="Party Pass lets you play full videos whenever you want without advertisements."
      />

      {/* YouTube-style Dedicated Fullscreen Video Player */}
      {isFullscreenPlayerOpen && (
        <FullscreenFeedPlayer
          isOpen={isFullscreenPlayerOpen}
          onClose={handleCloseFullscreenPlayer}
          videos={activeFeedVideos}
          currentIndex={fullscreenIndex}
          onIndexChange={handleFullscreenIndexChange}
          isLiked={likedVideos.has(activeFeedVideos[fullscreenIndex]?.id)}
          isDisliked={dislikedVideos.has(activeFeedVideos[fullscreenIndex]?.id)}
          onLike={handleLike}
          onDislike={handleDislike}
          isMutedGlobally={isMutedGlobally}
          setIsMutedGlobally={setIsMutedGlobally}
          playlistName={activeFeedTitle}
        />
      )}
    </div>
  );
};

export default MusicVideoFeed;